import { Layer, Stage } from "react-konva";
import type { BoardScene } from "@/lib/canvas/board-schema";
import { PitchBackground } from "./components/PitchBackground";
import { ShapeRenderer } from "./components/ShapeRenderer";
import { TokenRenderer } from "./components/TokenRenderer";

/**
 * Read-only tactical-board renderer.
 *
 * Sizes the Konva stage by the `width` prop at a 16:9 aspect — a
 * green field with the pitch markings drawn inset at true
 * proportions (`PitchBackground`). 16:9 so the read-only viewer
 * shares the editor's (`TacticalBoardEditor`) coordinate space:
 * tokens are normalized 0..1 of the SAME 16:9 stage on both sides,
 * so a board saved in the editor renders identically here.
 *
 * Used by:
 *   - Viewer surfaces (My Feedback, Player Development) where a board
 *     attached to a note renders inline as a preview.
 *   - The editor (`TacticalBoardEditor`) which wraps this and layers
 *     event handlers + the Konva.Transformer on top.
 *
 * Privacy invariant: the renderer must tolerate corrupted input
 * gracefully. The caller is expected to have run the scene through
 * `validateScene` already — but if a scene with malformed tokens or
 * shapes sneaks through, react-konva will silently no-op the
 * unrenderable nodes rather than crash.
 */

export type TacticalBoardViewProps = {
  scene: BoardScene;
  /** Render width in pixels. Height derives from the pitch aspect. */
  width: number;
  /** Optional override for the pitch fill color. */
  pitchColor?: string;
  appearance?: "grass" | "navy";
  /** When true, the Konva stage emits no events (pure decoration —
   * useful for screenshot capture and preview tiles). Defaults to
   * `false` so the editor's onClick handlers work. */
  passThrough?: boolean;
  /** When true, `kind: "label"` shapes are skipped. Used by chip
   * / thumbnail callers where Konva text renders at its source
   * font size and gets clipped to a few unreadable letters. */
  hideLabels?: boolean;
};

export function TacticalBoardView(props: TacticalBoardViewProps) {
  const width = Math.max(1, Math.round(props.width));
  const height = Math.max(1, Math.round(width * (9 / 16)));
  const dims = { width, height };

  const shapes = props.hideLabels
    ? props.scene.shapes.filter((s) => s.kind !== "label")
    : props.scene.shapes;

  return (
    <Stage
      width={width}
      height={height}
      listening={!props.passThrough}
      data-testid="tactical-board-stage"
    >
      <Layer listening={false}>
        <PitchBackground
          dims={dims}
          color={props.pitchColor}
          appearance={props.appearance}
        />
      </Layer>
      <Layer>
        {shapes.map((shape, i) => (
          <ShapeRenderer
            key={shape.id ?? `shape-${i}`}
            shape={shape}
            dims={dims}
          />
        ))}
        {props.scene.tokens.map((token, i) => (
          <TokenRenderer
            key={token.id ?? `token-${i}`}
            token={token}
            dims={dims}
          />
        ))}
      </Layer>
    </Stage>
  );
}
